/**
 * ImageGeneratorPanel — the reusable core of every AI-image generation surface.
 *
 * Extracted from the carousel `PostsTab` so that Posts & Carousels, the YouTube
 * custom-thumbnail panel, and any future surface render the SAME model-aware
 * config (provider / model / prompt / resolution / styles / advanced settings)
 * plus the SAME progress bar, generic error panel and generate→poll lifecycle.
 *
 * Why a single component (RULE #-2, no duplication):
 * - The model-aware config is identical to `ImageConfigForm` (reset steps/CFG on
 *   provider change, apply model-recommended steps/cfg, hide StyleSelector when
 *   the provider does not support styles). Both surfaces get it for free.
 * - The progress + generic error UI (the carousel "Bug 0" fix) lives here once,
 *   so it can never drift between surfaces again.
 *
 * What varies between surfaces is injected via props:
 * - `runner`     : how to start a generation and (optionally) how to poll it.
 *                  Carousel = POST + poll /status; thumbnail = POST /generate.
 * - `headerSlot` : surface-specific inputs ABOVE the config (carousel subject +
 *                  slide count; thumbnail keeps its title in the parent form).
 * - `extraControls` : surface-specific knobs BELOW the config (variation slider).
 * - `generateLabel` / `disabled` : action-button wording + gating.
 * - `children`   : the result UI (carousel slide grid; thumbnail preview/save).
 *
 * The image config itself is CONTROLLED by the parent (`config`/`onConfigChange`)
 * so the parent can translate it into its own API request shape.
 */

import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2, Sparkles } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { ImageProviderSelector } from './ImageProviderSelector';
import { ImageModelSelector } from './ImageModelSelector';
import { PromptInput } from './PromptInput';
import { ResolutionSelector } from './ResolutionSelector';
import { StyleSelector } from './StyleSelector';
import { AdvancedSettings } from './AdvancedSettings';
import { GenerationErrorPanel, ProgressBar } from '../common';
import {
  getProviderFeatures,
  FOOOCUS_ONLY_STYLES,
  IMAGE_GENERATION_OPERATION_LABEL_KEY,
  type AIImageProvider,
} from '../../constants/imageSource';
import { useTokenGate } from '../../hooks/useTokenGate';
import { HttpResponseError, isInsufficientTokensFailure } from '../../utils/httpUtils';
import type { ImageGenerationConfig, ImageModelInfo } from '../../types';

/**
 * Normalized progress shape every runner reports while a generation runs.
 * Mirrors `CarouselStatus` (progress 0-100 + human message) so the carousel
 * runner is a thin pass-through, while one-shot runners (thumbnail) can report
 * indeterminate progress with just a message.
 */
export interface ImageGenProgress {
  /** 0-100. Use a small constant (e.g. 50) for indeterminate one-shot runners. */
  progress: number;
  /** Human-readable status line shown next to the bar. */
  message: string;
}

/**
 * Runner contract — how a surface starts and (optionally) polls a generation.
 *
 * `start()` kicks off the work. It either:
 *   - returns `{ done: true, result }` immediately (one-shot, e.g. thumbnail), or
 *   - returns `{ done: false, jobId }` and the panel polls `poll(jobId)` until
 *     it reports `done` or `error` (async, e.g. carousel).
 *
 * Errors are surfaced by THROWING (start) or by returning `error` (poll). The
 * thrown/returned message is shown verbatim in the GenerationErrorPanel, so the
 * literal backend message (e.g. "Fooocus AI is not running…") reaches the user.
 *
 * ONE exception, handled by this panel rather than by each surface: if the
 * thrown error is an `HttpResponseError` whose signals say the account cannot
 * pay (402 / INSUFFICIENT_TOKENS), the panel opens the purchase modal instead
 * of printing the refusal. Runners must therefore throw `apiFailureError(resp,
 * …)` rather than `new Error(resp.error)`, which erases those signals.
 */
export interface ImageGenStartResult<TResult> {
  done: boolean;
  /** Present when `done` — the final result handed to `onComplete`. */
  result?: TResult;
  /** Present when not `done` — the id to poll. */
  jobId?: string;
  /** Optional initial progress snapshot. */
  progress?: ImageGenProgress;
}

export interface ImageGenPollResult<TResult> {
  /** 'processing' keeps polling; 'completed'/'error' stop it. */
  status: 'processing' | 'completed' | 'error';
  progress: ImageGenProgress;
  /** Present on 'completed'. */
  result?: TResult;
  /** Present on 'error' — shown verbatim in the error panel. */
  error?: string;
}

export interface ImageGenRunner<TResult> {
  start: (config: ImageGenerationConfig) => Promise<ImageGenStartResult<TResult>>;
  /** Required only for async surfaces (polling). One-shot surfaces omit it. */
  poll?: (jobId: string) => Promise<ImageGenPollResult<TResult>>;
  /** Poll cadence in ms. Defaults to 1500. */
  pollIntervalMs?: number;
}

interface ImageGeneratorPanelProps<TResult> {
  /** Controlled image config (provider/model/prompt/resolution/styles/advanced). */
  config: ImageGenerationConfig;
  onConfigChange: React.Dispatch<React.SetStateAction<ImageGenerationConfig>>;
  /** How to start + poll a generation for this surface. */
  runner: ImageGenRunner<TResult>;
  /** Called with the final result when a generation completes. */
  onComplete: (result: TResult) => void;
  /** Surface-specific inputs rendered ABOVE the image config (e.g. subject). */
  headerSlot?: React.ReactNode;
  /** Surface-specific knobs rendered BELOW the config (e.g. variation slider). */
  extraControls?: React.ReactNode;
  /**
   * Per-block visibility. A surface MUST hide any block its backend does not
   * actually honor, so we never render a control that silently does nothing
   * (RULE #22). E.g. the carousel endpoint accepts only provider+model today,
   * so it sets showPrompt/showStyles/showResolution/showAdvanced to false.
   * All default to true (the full model-aware config, used by the thumbnail).
   */
  showModel?: boolean;
  /** Whether to show the Prompt input. Default true. */
  showPrompt?: boolean;
  /** Whether to show the Resolution selector. Default true. */
  showResolution?: boolean;
  /** Whether to show the Style multi-select. Default true. */
  showStyles?: boolean;
  /** Whether to show the Advanced settings (steps/guidance/etc.). Default true. */
  showAdvanced?: boolean;
  /** Action-button label when idle. */
  generateLabel: string;
  /** Extra gating on top of the built-in prompt/loading checks. */
  disabled?: boolean;
  /** Require a non-empty prompt before allowing generation. Default false. */
  requirePrompt?: boolean;
  /** Result UI (slide grid / thumbnail preview), rendered under the controls. */
  children?: React.ReactNode;
  /** Stage label forwarded to GenerationErrorPanel for the copyable report. */
  errorStage?: string;
  className?: string;
  'data-testid'?: string;
}

function ImageGeneratorPanelInner<TResult>({
  config,
  onConfigChange,
  runner,
  onComplete,
  headerSlot,
  extraControls,
  showModel = true,
  showPrompt = true,
  showResolution = true,
  showStyles = true,
  showAdvanced = true,
  generateLabel,
  disabled = false,
  requirePrompt = false,
  children,
  errorStage,
  className = '',
  'data-testid': testId,
}: ImageGeneratorPanelProps<TResult>) {
  const { t } = useTranslation();
  const { reportInsufficientTokens } = useTokenGate();

  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useState<ImageGenProgress | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-model generation recommendations (e.g. Hyper checkpoints converge in
  // ~6 steps at low CFG). Surfaced as a hint next to the Steps slider.
  const [modelRecommendation, setModelRecommendation] = useState<{
    steps?: number;
    cfg?: number;
  } | null>(null);

  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const stopPolling = useCallback(() => {
    if (pollRef.current) {
      clearInterval(pollRef.current);
      pollRef.current = null;
    }
  }, []);

  useEffect(() => () => stopPolling(), [stopPolling]);

  const providerFeatures = useMemo(() => getProviderFeatures(config.provider), [config.provider]);

  const handleFieldChange = useCallback(
    <K extends keyof ImageGenerationConfig>(field: K, value: ImageGenerationConfig[K]) => {
      onConfigChange((prev) => ({ ...prev, [field]: value }));
    },
    [onConfigChange]
  );

  // Provider change — reset provider-specific fields (identical to ImageConfigForm).
  const handleProviderChange = useCallback(
    (provider: AIImageProvider) => {
      const features = getProviderFeatures(provider);
      setModelRecommendation(null);
      onConfigChange((prev) => ({
        ...prev,
        provider,
        // A checkpoint id from the previous provider is never valid for the new
        // one — reset and let ImageModelSelector auto-select the new default.
        model: undefined,
        styles: features.supportsStyles
          ? provider === 'fooocus'
            ? prev.styles
            : prev.styles.filter(
                (s) => !FOOOCUS_ONLY_STYLES.includes(s as (typeof FOOOCUS_ONLY_STYLES)[number])
              )
          : [],
        num_steps: features.defaultNumSteps,
        guidance_scale: features.defaultGuidance,
      }));
    },
    [onConfigChange]
  );

  const handleResolutionChange = useCallback(
    (preset: string, width?: number, height?: number) => {
      onConfigChange((prev) => ({
        ...prev,
        resolution: preset === 'custom' ? `${width}x${height}` : preset,
        width: preset === 'custom' ? width : undefined,
        height: preset === 'custom' ? height : undefined,
      }));
    },
    [onConfigChange]
  );

  const handleModelChange = useCallback(
    (model: string, meta?: ImageModelInfo) => {
      onConfigChange((prev) => {
        const next = { ...prev, model };
        // Apply the checkpoint's recommended generation params. Critical for
        // Hyper models: leaving CFG at the standard 7.0 burns the image.
        if (typeof meta?.recommended_steps === 'number') {
          next.num_steps = meta.recommended_steps;
        }
        if (typeof meta?.recommended_cfg === 'number') {
          next.guidance_scale = meta.recommended_cfg;
        }
        return next;
      });
      setModelRecommendation(
        meta && (meta.recommended_steps != null || meta.recommended_cfg != null)
          ? { steps: meta.recommended_steps, cfg: meta.recommended_cfg }
          : null
      );
    },
    [onConfigChange]
  );

  /**
   * Turn a thrown runner failure into what the user should see.
   *
   * A refusal for lack of tokens opens the purchase modal and sets a short,
   * translated line in the error panel; anything else keeps the existing
   * behaviour of showing the backend's own message. `null` is passed as the
   * required amount because these surfaces (carousel, thumbnail) display no
   * quote — the modal omits the deficit line rather than invent one.
   */
  const surfaceStartError = useCallback(
    (err: unknown): void => {
      if (
        err instanceof HttpResponseError &&
        isInsufficientTokensFailure({ errorCode: err.code, status: err.status })
      ) {
        reportInsufficientTokens(
          null,
          t(IMAGE_GENERATION_OPERATION_LABEL_KEY, { defaultValue: 'AI Image Generation' })
        );
        setError(t('tokens.insufficientTokens', { defaultValue: 'Insufficient Tokens' }));
        return;
      }
      setError(
        err instanceof Error
          ? err.message
          : t('image.generator.error', { defaultValue: 'Generation failed' })
      );
    },
    [reportInsufficientTokens, t]
  );

  const handleGenerate = useCallback(async () => {
    if (requirePrompt && !config.prompt.trim()) return;
    setError(null);
    setProgress(null);
    setIsGenerating(true);
    stopPolling();

    try {
      const startResult = await runner.start(config);

      if (startResult.progress) {
        setProgress(startResult.progress);
      }

      // One-shot surface (e.g. thumbnail): result is ready immediately.
      if (startResult.done) {
        setIsGenerating(false);
        if (startResult.result !== undefined) {
          onComplete(startResult.result);
        }
        return;
      }

      // Async surface (e.g. carousel): poll until completed / error.
      const jobId = startResult.jobId;
      if (!jobId || !runner.poll) {
        setIsGenerating(false);
        setError(
          t('image.generator.missingPoller', {
            defaultValue: 'Generation started but no status channel is available.',
          })
        );
        return;
      }

      const poll = runner.poll;
      const interval = runner.pollIntervalMs ?? 1500;
      pollRef.current = setInterval(() => {
        void (async () => {
          try {
            const statusResult = await poll(jobId);
            setProgress(statusResult.progress);
            if (statusResult.status === 'completed') {
              stopPolling();
              setIsGenerating(false);
              if (statusResult.result !== undefined) {
                onComplete(statusResult.result);
              }
            } else if (statusResult.status === 'error') {
              stopPolling();
              setIsGenerating(false);
              setError(
                statusResult.error ??
                  t('image.generator.error', { defaultValue: 'Generation failed' })
              );
            }
          } catch (err) {
            stopPolling();
            setIsGenerating(false);
            surfaceStartError(err);
          }
        })();
      }, interval);
    } catch (err) {
      setIsGenerating(false);
      surfaceStartError(err);
    }
  }, [config, requirePrompt, runner, onComplete, stopPolling, t, surfaceStartError]);

  const generateDisabled = disabled || isGenerating || (requirePrompt && !config.prompt.trim());

  return (
    <div className={`space-y-6 ${className}`} data-testid={testId}>
      {/* Surface-specific header inputs (e.g. carousel subject + slide count). */}
      {headerSlot}

      {/* Provider — always visible. */}
      <ImageProviderSelector
        value={config.provider}
        onChange={handleProviderChange}
        disabled={isGenerating}
      />

      {/* Model — only for providers that expose a checkpoint catalog. */}
      {showModel && providerFeatures.supportsModels && (
        <ImageModelSelector
          value={config.model}
          onChange={handleModelChange}
          provider={config.provider}
          disabled={isGenerating}
        />
      )}

      {/* Prompt — empty = AI generates; filled = override. */}
      {showPrompt && (
        <PromptInput
          prompt={config.prompt}
          negativePrompt={providerFeatures.supportsNegativePrompt ? config.negative_prompt : ''}
          onPromptChange={(value) => handleFieldChange('prompt', value)}
          onNegativePromptChange={(value) => handleFieldChange('negative_prompt', value)}
          disabled={isGenerating}
          showNegativePrompt={providerFeatures.supportsNegativePrompt}
        />
      )}

      {/* Resolution. */}
      {showResolution && (
        <ResolutionSelector
          value={config.resolution}
          customWidth={config.width}
          customHeight={config.height}
          onChange={handleResolutionChange}
          disabled={isGenerating}
          provider={config.provider}
        />
      )}

      {/* Styles — only for providers that support them. */}
      {showStyles && providerFeatures.supportsStyles && (
        <StyleSelector
          selectedStyles={config.styles}
          onChange={(styles) => handleFieldChange('styles', styles)}
          maxStyles={10}
          disabled={isGenerating}
          provider={config.provider}
        />
      )}

      {/* Advanced — provider-aware (steps/guidance/sharpness/performance). */}
      {showAdvanced && (
        <AdvancedSettings
          numImages={config.num_images}
          seed={config.seed}
          guidanceScale={config.guidance_scale}
          numSteps={config.num_steps}
          sharpness={config.sharpness}
          performance={config.performance}
          outputFormat={config.output_format}
          onNumImagesChange={(value) => handleFieldChange('num_images', value)}
          onSeedChange={(value) => handleFieldChange('seed', value)}
          onGuidanceScaleChange={(value) => handleFieldChange('guidance_scale', value)}
          onNumStepsChange={(value) => handleFieldChange('num_steps', value)}
          onSharpnessChange={(value) => handleFieldChange('sharpness', value)}
          onPerformanceChange={(value) => handleFieldChange('performance', value)}
          onOutputFormatChange={(value) => handleFieldChange('output_format', value)}
          disabled={isGenerating}
          showSeed={providerFeatures.supportsSeed}
          showNumSteps={providerFeatures.supportsNumSteps}
          showGuidanceScale={providerFeatures.supportsGuidance}
          showSharpness={providerFeatures.supportsSharpness}
          showPerformance={providerFeatures.supportsPerformance}
          showOutputFormat={providerFeatures.supportsOutputFormat}
          maxNumSteps={providerFeatures.maxNumSteps}
          maxGuidanceScale={providerFeatures.maxGuidance}
          recommendedSteps={modelRecommendation?.steps}
          recommendedGuidanceScale={modelRecommendation?.cfg}
        />
      )}

      {/* Surface-specific knobs (e.g. variation_count slider). */}
      {extraControls}

      {/* Generate. */}
      <button
        type="button"
        onClick={() => void handleGenerate()}
        disabled={generateDisabled}
        className="w-full min-h-[48px] inline-flex items-center justify-center gap-2 rounded-lg bg-purple-600 hover:bg-purple-500 text-white font-medium disabled:opacity-40"
        data-testid="image-generator-generate-btn"
      >
        {isGenerating ? (
          <Loader2 className="h-5 w-5 animate-spin" />
        ) : (
          <Sparkles className="h-5 w-5" />
        )}
        {isGenerating
          ? t('image.generator.generating', { defaultValue: 'Generating...' })
          : generateLabel}
      </button>

      {/* Progress (Bug 0 item 2) — shared by every surface. */}
      {isGenerating && progress && (
        <div className="bg-black/20 rounded-lg p-4 border border-white/10">
          <div className="flex justify-between text-xs text-white/60 mb-2">
            <span>{progress.message}</span>
            <span>{Math.round(progress.progress)}%</span>
          </div>
          <ProgressBar percent={progress.progress} color="bg-purple-500" />
        </div>
      )}

      {/* Generic error (Bug 0 item 1) — verbatim backend message + copy report. */}
      {error && <GenerationErrorPanel error={error} stage={errorStage} />}

      {/* Surface-specific result UI. */}
      {children}
    </div>
  );
}

export const ImageGeneratorPanel = memo(
  ImageGeneratorPanelInner
) as typeof ImageGeneratorPanelInner;

export default ImageGeneratorPanel;
